import { Form, Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../../components/Breadcrumbs';
import Button from '../../components/button';
import FileUpload from '../../components/FileUpload';
import Input from '../../components/input';
import Select from '../../components/Select';
import { findCollectionByKeys, normalizeOptions } from '../../utils/helper';
import { fetchIssueEquipmentDropdown, fetchIssueTyresDropdown, fetchIssueVehiclesDropdown } from '../../store/Slices/IssuesSlice';
import { clearEquipmentDetailState, fetchEquipmentServiceDetail } from '../../store/Slices/EquipmentSlice';
import { clearVehicleDetail, fetchServiceTagsDropdown, fetchVehicleDetail, getSingleService, updateServiceEntry } from '../../store/Slices/ServiceSlice';
import { clearTyreDetailState, fetchTyreServiceDetail } from '../../store/Slices/TyreSlice';
import '../../styles/ServiceEntry.css';
import { maintenanceTypesDropdown, repairPriorityDropdown, serviceStatusOptions } from '../../utils/Dropdowns';
import { formatUtcDateTimeForApi, formatUtcDateTimeForInput, hasPermission } from '../../utils/helper';
import { createServiceEntryValidationSchema } from '../../utils/Schema';
import { errorNotify, successNotify } from '../../utils/toast';
import IssueTracking from './IssueTracking';
import ServiceLineItem from './ServiceLineItem';
import { AppMainModules, MaintinaceSubModules, ServicesPermission } from '../../utils/app-constant';

const AssetSourceTypeEnum = {
    Vehicle: 'vehicle',
    Equipment: 'equipment',
    Tyre: 'tyre'
};

const assetTypeOptions = [
    { label: 'Vehicle', value: AssetSourceTypeEnum.Vehicle },
    { label: 'Equipment', value: AssetSourceTypeEnum.Equipment },
    { label: 'Tyre', value: AssetSourceTypeEnum.Tyre }
];

const serviceWorkflowAssetTypes = [
    AssetSourceTypeEnum.Vehicle,
    AssetSourceTypeEnum.Equipment,
    AssetSourceTypeEnum.Tyre
];

const getPayloadId = (value) => {
    if (value === '' || value === null || value === undefined) {
        return value;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : value;
};

const getNormalizedNumericValue = (value) => {
    if (value === '' || value === null || value === undefined) {
        return null;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const extractAssetMeterValue = (detailData) => {
    const candidates = [
        detailData?.odm,
        detailData?.odo_meter,
        detailData?.usage_meter_reading,
        detailData?.usageMeterReading,
        detailData?.meter_reading,
        detailData?.distance,
        detailData?.distance_covered,
        detailData?.distanceCovered,
        detailData?.total_km_run,
        detailData?.totalKmRun,
        detailData?.covered_distance,
        detailData?.vehicle?.odm,
        detailData?.vehicle?.odo_meter,
        detailData?.last_service?.odo_meter
    ];

    for (const candidate of candidates) {
        if (candidate === '' || candidate === null || candidate === undefined) {
            continue;
        }

        return candidate;
    }

    return '';
};

const getAssetServiceLineItems = (detailData) => {
    if (Array.isArray(detailData?.asset_service_line_items)) {
        return detailData.asset_service_line_items;
    }

    if (Array.isArray(detailData?.service_line_items)) {
        return detailData.service_line_items;
    }

    return [];
};

const calculateCostSummary = (detailData, fallbackData = null) => {
    const items = getAssetServiceLineItems(detailData);

    if (!items.length) {
        return {
            cost_parts: fallbackData?.cost_parts || fallbackData?.rate || 0,
            cost_labor: fallbackData?.cost_labor || 0,
            sub_total: fallbackData?.sub_total || 0,
            discount: fallbackData?.discount || 0,
            tax: fallbackData?.cost_tax || 0,
        };
    }

    const cost_parts = items.reduce(
        (sum, item) => sum + (parseFloat(item?.rate || 0) * parseFloat(item?.qty || 0)),
        0
    );
    const cost_labor = items.reduce((sum, item) => sum + parseFloat(item?.cost_labor || 0), 0);
    const sub_total = cost_parts + cost_labor;

    return {
        cost_parts: cost_parts.toFixed(2),
        cost_labor: cost_labor.toFixed(2),
        sub_total: sub_total.toFixed(2)
    };
};

const resolveAssetType = (serviceData) => {
    const normalizedAssetType = String(serviceData?.asset_type || '').trim().toLowerCase();

    if (serviceWorkflowAssetTypes.includes(normalizedAssetType)) {
        return normalizedAssetType;
    }

    if (serviceData?.equipment || getNormalizedNumericValue(serviceData?.equipment_id) !== null) {
        return AssetSourceTypeEnum.Equipment;
    }

    if (serviceData?.tyre || getNormalizedNumericValue(serviceData?.tyre_id) !== null) {
        return AssetSourceTypeEnum.Tyre;
    }

    return AssetSourceTypeEnum.Vehicle;
};

const getAssetTypeLabel = (assetType) => {
    if (assetType === AssetSourceTypeEnum.Equipment) return 'Equipment';
    if (assetType === AssetSourceTypeEnum.Tyre) return 'Tyre';
    return 'Vehicle';
};

const buildSelectedAssetOption = (serviceData, assetType) => {
    if (!serviceData) return null;

    if (assetType === AssetSourceTypeEnum.Equipment) {
        const equipment = serviceData?.equipment || {};
        const optionValue =
            getPayloadId(equipment?.id)
            ?? getPayloadId(serviceData?.equipment_id)
            ?? getPayloadId(serviceData?.asset_id);

        if (optionValue === '' || optionValue === null || optionValue === undefined) {
            return null;
        }

        return {
            value: optionValue,
            label:
                equipment?.equipment_id
                || equipment?.title
                || equipment?.name
                || equipment?.serial_number
                || `Equipment ${optionValue}`
        };
    }

    if (assetType === AssetSourceTypeEnum.Tyre) {
        const tyre = serviceData?.tyre || {};
        const optionValue =
            getPayloadId(tyre?.id)
            ?? getPayloadId(serviceData?.tyre_id)
            ?? getPayloadId(serviceData?.asset_id);

        if (optionValue === '' || optionValue === null || optionValue === undefined) {
            return null;
        }

        return {
            value: optionValue,
            label:
                tyre?.tyres_id
                || tyre?.serial_number
                || tyre?.system_id
                || `Tyre ${optionValue}`
        };
    }

    const vehicle = serviceData?.vehicle || {};
    const optionValue =
        getPayloadId(vehicle?.id)
        ?? getPayloadId(serviceData?.vehicle_id)
        ?? getPayloadId(serviceData?.asset_id);

    if (optionValue === '' || optionValue === null || optionValue === undefined) {
        return null;
    }

    return {
        value: optionValue,
        label:
            vehicle?.registration_number
            || vehicle?.registration_no
            || vehicle?.vehicle_number
            || `Vehicle ${optionValue}`,
        make_id: vehicle?.make_id ?? vehicle?.brand_id ?? serviceData?.vehicle_brand_id,
        brand_id: vehicle?.brand_id ?? vehicle?.make_id ?? serviceData?.vehicle_brand_id,
        ref_id: serviceData?.id
    };
};

const EditServiceEntry = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const [selectedAssetType, setSelectedAssetType] = useState('');
    const [selectedAssetOption, setSelectedAssetOption] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [link_issue_ids, setLink_issue_ids] = useState([]);
    const [updateIssue, setUpdateIssue] = useState(false);
    const [checkedIssueIds, setCheckedIssueIds] = useState(() => {
        // Load from localStorage on mount
        const saved = localStorage.getItem(`checkedIssueIds_edit_${id}`);
        return saved ? JSON.parse(saved) : [];
    });

    const {
        vehicleDetail,
        vehicleDetailLoading,
        singleServiceData,
        singleServiceLoading,
        serviceTagsData,
        serviceTagsLoading,
        updateServiceEntryLoading
    } = useSelector((state) => state.services);
    const {
        equipmentDetailData,
        equipmentDetailLoading,
    } = useSelector((state) => state.equipment);
    const {
        tyreDetailData,
        tyreDetailLoading,
    } = useSelector((state) => state.tyre);
    const {
        issueVehiclesDropdownData,
        issueVehiclesDropdownLoading,
        issueEquipmentDropdownData,
        issueEquipmentDropdownLoading,
        issueTyresDropdownData,
        issueTyresDropdownLoading,
    } = useSelector((state) => state.issues);

    const [discountPercent, setDiscountPercent] = useState(singleServiceData?.discount || 0);
    const [taxPercent, setTaxPercent] = useState(singleServiceData?.cost_tax || 0);

    const canUpdate = hasPermission({ permissionSlug: ServicesPermission.Create, menuSlug: MaintinaceSubModules.Services, moduleSlug: AppMainModules.maintenance });

    useEffect(() => {
        dispatch(getSingleService(id));
        dispatch(fetchIssueVehiclesDropdown());
        dispatch(fetchIssueEquipmentDropdown());
        dispatch(fetchIssueTyresDropdown());
        dispatch(fetchServiceTagsDropdown());

        return () => {
            dispatch(clearVehicleDetail());
            dispatch(clearEquipmentDetailState());
            dispatch(clearTyreDetailState());
            // Clear localStorage on unmount
            const storageKey = `checkedIssueIds_edit_${id}`;
            localStorage.removeItem(storageKey);
        }
    }, [dispatch, id]);

    const vehicleOptions = useMemo(() => {
        const source = Array.isArray(issueVehiclesDropdownData)
            ? issueVehiclesDropdownData
            : findCollectionByKeys(issueVehiclesDropdownData, [
                'vehicles',
                'vehicle',
                'vehiclesdropdown',
                'vehicledropdown',
                'vehiclelist',
            ]);

        return normalizeOptions(source, {
            valueKeys: ['value', 'id', 'vehicle_id', 'ref_id'],
            labelKeys: ['label', 'registration_number', 'registration_no', 'vehicle_registration_number', 'name', 'title'],
            defaultLabelPrefix: 'Vehicle'
        });
    }, [issueVehiclesDropdownData]);

    const equipmentOptions = useMemo(() => {
        const source = Array.isArray(issueEquipmentDropdownData)
            ? issueEquipmentDropdownData
            : findCollectionByKeys(issueEquipmentDropdownData, [
                'equipments',
                'equipmentdropdown',
                'equipment',
                'equipmentlist',
            ]);

        return normalizeOptions(source, {
            valueKeys: ['value', 'id', 'equipment_id'],
            labelKeys: ['label', 'equipment_id', 'title', 'name', 'serial_number', 'registration_number'],
            defaultLabelPrefix: 'Equipment'
        });
    }, [issueEquipmentDropdownData]);

    const tyreOptions = useMemo(() => {
        const source = Array.isArray(issueTyresDropdownData)
            ? issueTyresDropdownData
            : findCollectionByKeys(issueTyresDropdownData, [
                'tyres',
                'tyre',
                'tyresdropdown',
                'tyredropdown',
                'tyrelist',
            ]);

        return normalizeOptions(source, {
            valueKeys: ['value', 'id', 'tyre_id', 'system_id'],
            labelKeys: ['label', 'tyres_id', 'tyre_id', 'serial_number', 'system_id', 'name', 'title'],
            defaultLabelPrefix: 'Tyre'
        });
    }, [issueTyresDropdownData]);

    useEffect(() => {
        if (!singleServiceData) return;

        const nextAssetType = resolveAssetType(singleServiceData);
        const nextSelectedAssetOption = buildSelectedAssetOption(singleServiceData, nextAssetType);

        setSelectedAssetType(nextAssetType);
        setSelectedAssetOption(nextSelectedAssetOption);

        dispatch(clearVehicleDetail());
        dispatch(clearEquipmentDetailState());
        dispatch(clearTyreDetailState());

        if (nextAssetType === AssetSourceTypeEnum.Vehicle && nextSelectedAssetOption?.value) {
            dispatch(fetchVehicleDetail({
                vehicleId: nextSelectedAssetOption.value,
                vehicleBrandId: nextSelectedAssetOption.make_id ?? nextSelectedAssetOption.brand_id,
                ref_id: singleServiceData.id
            }));
        } else if (nextAssetType === AssetSourceTypeEnum.Equipment && nextSelectedAssetOption?.value) {
            dispatch(fetchEquipmentServiceDetail({
                equipmentId: nextSelectedAssetOption.value,
                serviceId: singleServiceData.id
            }));
        } else if (nextAssetType === AssetSourceTypeEnum.Tyre && nextSelectedAssetOption?.value) {
            dispatch(fetchTyreServiceDetail({
                tyreId: nextSelectedAssetOption.value,
                serviceId: singleServiceData.id
            }));
        }

        if (singleServiceData?.images) {
            setUploadedFiles(singleServiceData?.images.map(img => ({
                fileName: img.file_path,
                originalName: img.file_path,
                previewUrl: null,
                backend: true,
                id: img.id
            })));
        }

        setDiscountPercent(parseInt(singleServiceData?.discount) || 0);
        setTaxPercent(parseInt(singleServiceData?.cost_tax) || 0);

    }, [dispatch, singleServiceData]);

    // Save to localStorage whenever checkedIssueIds changes
    useEffect(() => {
        const storageKey = `checkedIssueIds_edit_${id}`;
        localStorage.setItem(storageKey, JSON.stringify(checkedIssueIds));
    }, [checkedIssueIds, id]);

    const tagsOptions = serviceTagsData?.map(tag => ({ label: tag.tag_name, value: tag.tag_name }));

    // Prepare tags array
    const tagsArr = singleServiceData?.tags
        ? Array.isArray(singleServiceData.tags)
            ? singleServiceData.tags
            : [{ value: singleServiceData.tags.tag_name, label: singleServiceData.tags.tag_name }]
        : [];

    const getCurrentAssetDetail = (assetType) => {
        if (assetType === AssetSourceTypeEnum.Vehicle) {
            return vehicleDetail;
        }

        if (assetType === AssetSourceTypeEnum.Equipment) {
            return equipmentDetailData;
        }

        if (assetType === AssetSourceTypeEnum.Tyre) {
            return tyreDetailData;
        }

        return null;
    };

    const getCurrentAssetDetailLoading = (assetType) => {
        if (assetType === AssetSourceTypeEnum.Vehicle) {
            return vehicleDetailLoading;
        }

        if (assetType === AssetSourceTypeEnum.Equipment) {
            return equipmentDetailLoading;
        }

        if (assetType === AssetSourceTypeEnum.Tyre) {
            return tyreDetailLoading;
        }

        return false;
    };

    const initialValues = {
        asset_type: selectedAssetType || (singleServiceData ? resolveAssetType(singleServiceData) : ''),
        asset_id:
            getPayloadId(singleServiceData?.asset_id)
            ?? getPayloadId(selectedAssetOption?.value)
            ?? '',
        odm: singleServiceData?.odo_meter ?? extractAssetMeterValue(getCurrentAssetDetail(selectedAssetType)) ?? '',
        odo_meter: singleServiceData?.odo_meter ?? '',
        status: singleServiceData?.status || '',
        ref_number: singleServiceData?.ref_number || '',
        priority: singleServiceData?.priority || '',
        services_type: singleServiceData?.services_type || '',
        start_at: singleServiceData?.start_at ? formatUtcDateTimeForInput(singleServiceData.start_at).slice(0, 10) : '',
        complete_at: formatUtcDateTimeForInput(singleServiceData?.complete_at),
        note: singleServiceData?.note || '',
        tags: tagsArr
    };

    const handleDiscountChange = (e) => {
        let val = e.target.value.replace(/[^0-9.]/g, '');
        val = val === '' ? '' : Math.max(0, Math.min(100, Number(val)));
        setDiscountPercent(val);
    };
    const handleTaxChange = (e) => {
        let val = e.target.value.replace(/[^0-9.]/g, '');
        val = val === '' ? '' : Math.max(0, Math.min(100, Number(val)));
        setTaxPercent(val);
    };

    const calculateTotal = () => {
        const currentAssetDetail = getCurrentAssetDetail(selectedAssetType);
        const costSummary = calculateCostSummary(currentAssetDetail, singleServiceData);
        const subTotal = parseFloat(costSummary.sub_total);
        const discount = parseFloat(discountPercent) || 0;
        const tax = parseFloat(taxPercent) || 0;
        const discountAmount = subTotal * (discount / 100);
        const taxAmount = subTotal * (tax / 100);
        return (subTotal + taxAmount - discountAmount).toFixed(2);
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const currentAssetType = values.asset_type || selectedAssetType || resolveAssetType(singleServiceData);
            const currentAssetDetail = getCurrentAssetDetail(currentAssetType);
            const currentLineItems = getAssetServiceLineItems(currentAssetDetail);
            const supportsServiceWorkflow = serviceWorkflowAssetTypes.includes(currentAssetType);
            const costSummary = calculateCostSummary(currentAssetDetail, singleServiceData);

            if (supportsServiceWorkflow && currentLineItems.length === 0) {
                errorNotify("Atleast one Service Line Item is required!");
                return;
            }

            // Validate discount/tax percent
            if (discountPercent < 0 || discountPercent > 100) {
                errorNotify("Discount must be between 0 and 100%");
                return;
            }
            if (taxPercent < 0 || taxPercent > 100) {
                errorNotify("Tax must be between 0 and 100%");
                return;
            }

            const subTotal = parseFloat(costSummary.sub_total);
            const discount = parseFloat(discountPercent) || 0;
            const tax = parseFloat(taxPercent) || 0;
            const discountAmount = subTotal * (discount / 100);
            const taxAmount = subTotal * (tax / 100);
            const complete_total = subTotal + taxAmount - discountAmount;
            const meterValue = parseInt(values.odm, 10);

            const serviceData = {
                maintenances_service_id: singleServiceData?.id,
                asset_id: getPayloadId(values.asset_id),
                asset_type: currentAssetType,
                ...(meterValue === null || Number.isNaN(meterValue) ? {} : { odo_meter: meterValue }),
                ...(currentAssetType === AssetSourceTypeEnum.Vehicle && meterValue !== null && !Number.isNaN(meterValue)
                    ? { odm: meterValue }
                    : {}),
                priority: values.priority,
                ref_number: `SE-${singleServiceData?.id}`,
                services_type: values.services_type,
                status: values.status,
                start_at: formatUtcDateTimeForApi(values.start_at),
                complete_at: formatUtcDateTimeForApi(values.complete_at),
                note: values.note,
                cost_parts: parseFloat(costSummary.cost_parts),
                cost_labor: parseFloat(costSummary.cost_labor),
                cost_tax: taxPercent,
                discount: discountPercent,
                sub_total: subTotal,
                complete_total: complete_total,
                tags: values.tags.map(tag => tag.value || tag),
                files: uploadedFiles.map(file => file.fileName),
                link_issue_ids: supportsServiceWorkflow ? link_issue_ids : [],
                line_item: supportsServiceWorkflow ? currentLineItems.map(item => ({
                    line_item_id: item.id,
                    service_issue_id: item.service_issue_id
                })) : [],
                updatedKeys: updateIssue ? ['link_issue_ids'] : []
            };

            await dispatch(updateServiceEntry(serviceData)).unwrap();
            successNotify('Service entry updated successfully');

            // Clear localStorage after successful save
            const storageKey = `checkedIssueIds_edit_${id}`;
            localStorage.removeItem(storageKey);

            resetForm();
            setSelectedAssetType('');
            setSelectedAssetOption(null);
            setUploadedFiles([]);
            setLink_issue_ids([]);
            setCheckedIssueIds([]);
            dispatch(clearVehicleDetail());
            dispatch(clearEquipmentDetailState());
            dispatch(clearTyreDetailState());
            navigate('/services');
        } catch (error) {
            errorNotify(error);
        } finally {
            setSubmitting(false);
        }
    };

    const refreshVehicleDetail = async (source = 'both', refIdOverride = null) => {
        if (selectedAssetType === AssetSourceTypeEnum.Vehicle && selectedAssetOption?.value) {
            const response = await dispatch(fetchVehicleDetail({
                vehicleId: selectedAssetOption.value,
                vehicleBrandId: selectedAssetOption.make_id ?? selectedAssetOption.brand_id,
                ref_id: refIdOverride ?? singleServiceData?.id,
            }));
            return { response, source };
        }

        if (selectedAssetType === AssetSourceTypeEnum.Equipment && selectedAssetOption?.value) {
            const response = await dispatch(fetchEquipmentServiceDetail({
                equipmentId: selectedAssetOption.value,
                serviceId: refIdOverride ?? singleServiceData?.id,
            }));
            return { response, source };
        }

        if (selectedAssetType === AssetSourceTypeEnum.Tyre && selectedAssetOption?.value) {
            const response = await dispatch(fetchTyreServiceDetail({
                tyreId: selectedAssetOption.value,
                serviceId: refIdOverride ?? singleServiceData?.id,
            }));
            return { response, source };
        }
    };

    if (singleServiceLoading || !singleServiceData) {
        return <div className='loading_spinner'><Spinner animation="grow" /></div>;
    }

    return (
        <>
            <Breadcrumbs values={['Maintenance', 'Overview', 'Edit Service Entry']} />

            <Formik
                initialValues={initialValues}
                validationSchema={createServiceEntryValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
            >
                {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => {
                    const currentAssetType = values.asset_type || selectedAssetType || resolveAssetType(singleServiceData);
                    const isVehicleAsset = currentAssetType === AssetSourceTypeEnum.Vehicle;
                    const supportsServiceWorkflow = serviceWorkflowAssetTypes.includes(currentAssetType);
                    const currentAssetOptions =
                        currentAssetType === AssetSourceTypeEnum.Vehicle
                            ? vehicleOptions
                            : currentAssetType === AssetSourceTypeEnum.Equipment
                                ? equipmentOptions
                                : currentAssetType === AssetSourceTypeEnum.Tyre
                                    ? tyreOptions
                                    : [];
                    const currentAssetLoading =
                        currentAssetType === AssetSourceTypeEnum.Vehicle
                            ? issueVehiclesDropdownLoading
                            : currentAssetType === AssetSourceTypeEnum.Equipment
                                ? issueEquipmentDropdownLoading
                                : currentAssetType === AssetSourceTypeEnum.Tyre
                                    ? issueTyresDropdownLoading
                                    : false;
                    const currentAssetLabel = getAssetTypeLabel(currentAssetType);
                    const selectedAssetValue =
                        currentAssetOptions.find(
                            (option) => String(option.value) === String(values.asset_id)
                        ) || selectedAssetOption || null;
                    const currentAssetDetail = getCurrentAssetDetail(currentAssetType);
                    const currentAssetDetailLoading = getCurrentAssetDetailLoading(currentAssetType);
                    const costSummary = calculateCostSummary(currentAssetDetail, singleServiceData);
                    const totalAmount = calculateTotal();

                    return (
                    <Form>
                        <div className='access_view'>
                            <h2>Edit Service Entry</h2>
                            <div className='service_entry_btn'>
                                <Button
                                    cancelBtn={() => navigate('/services')}
                                    loading={updateServiceEntryLoading || isSubmitting}
                                    disabled={updateServiceEntryLoading || isSubmitting}
                                    submitText="Update Service Entry"
                                    type="submit"
                                    shouldRow={false}
                                    isShowSubmitBtn={canUpdate}
                                />
                            </div>
                        </div>

                        <div className='service_entry_main'>
                            <div className='container-fluid'>
                                <div className='row'>
                                    <div className='col-md-8'>
                                        <div className='service_entry_details'>
                                            <h6>Service Entry Details</h6>
                                            <p>Edit information for this service record.</p>

                                            <div className='vehicle_details_fields'>
                                                <h5>Asset Details</h5>
                                                <Row>
                                                    <Col md={6}>
                                                        <Select
                                                            label="Assets Type"
                                                            placeholder="Select Asset Type"
                                                            options={assetTypeOptions}
                                                            loading={false}
                                                            name="asset_type"
                                                            value={assetTypeOptions.find(option => option.value === currentAssetType)}
                                                            onChange={() => { }}
                                                            onBlur={handleBlur}
                                                            error={errors.asset_type}
                                                            touched={touched.asset_type}
                                                            isDisabled={true}
                                                            isSearch={false}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Select
                                                            label={currentAssetLabel}
                                                            placeholder={`Select ${currentAssetLabel}`}
                                                            options={currentAssetOptions}
                                                            loading={currentAssetLoading}
                                                            name="asset_id"
                                                            value={selectedAssetValue}
                                                            onChange={() => { }}
                                                            onBlur={handleBlur}
                                                            error={errors.asset_id}
                                                            touched={touched.asset_id}
                                                            isDisabled={true}
                                                            isSearch={false}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Select
                                                            label="Repair Priority"
                                                            placeholder="Select Repair Priority"
                                                            options={repairPriorityDropdown}
                                                            loading={false}
                                                            name="priority"
                                                            value={repairPriorityDropdown.find(option => option.value === values.priority)}
                                                            onChange={(selectedOption) => setFieldValue('priority', selectedOption?.value || '')}
                                                            onBlur={handleBlur}
                                                            error={errors.priority}
                                                            touched={touched.priority}
                                                            isSearch={false}
                                                        />
                                                    </Col>
                                                    {currentAssetType && (
                                                        <Col md={6}>
                                                            <Input
                                                                label="Odometer Reading / Distance"
                                                                placeholder="Enter Odometer Reading / Distance"
                                                                type="number"
                                                                name="odm"
                                                                value={values.odm}
                                                                showExtraText={
                                                                    isVehicleAsset &&
                                                                    values.odo_meter !== '' &&
                                                                    values.odo_meter !== null &&
                                                                    values.odo_meter !== undefined &&
                                                                    String(values.odo_meter) !== String(values.odm)
                                                                }
                                                                extraText={`Last Recorded: ${values?.odo_meter || 0} KM`}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={errors.odm}
                                                                touched={touched.odm}
                                                            />
                                                        </Col>
                                                    )}
                                                    <Col md={6}>
                                                        <Select
                                                            label="Maintenance Type"
                                                            placeholder="Select Maintenance Type"
                                                            options={maintenanceTypesDropdown}
                                                            loading={false}
                                                            name="services_type"
                                                            value={maintenanceTypesDropdown.find(option => option.value === values.services_type)}
                                                            onChange={(selectedOption) => setFieldValue('services_type', selectedOption?.value || '')}
                                                            onBlur={handleBlur}
                                                            error={errors.services_type}
                                                            touched={touched.services_type}
                                                            isSearch={false}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Input
                                                            label="Reference Number"
                                                            placeholder="Enter Reference"
                                                            type="text"
                                                            name="ref_number"
                                                            value={values.ref_number}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            error={errors.ref_number}
                                                            touched={touched.ref_number}
                                                            disabled
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Select
                                                            label="Status"
                                                            placeholder="Select Status"
                                                            options={serviceStatusOptions}
                                                            loading={false}
                                                            name="status"
                                                            value={serviceStatusOptions.find(option => option.value === values.status)}
                                                            onChange={(selectedOption) => setFieldValue('status', selectedOption?.value || '')}
                                                            onBlur={handleBlur}
                                                            error={errors.status}
                                                            touched={touched.status}
                                                            isSearch={false}
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>

                                            <div className='vehicle_details_fields'>
                                                <h5>Repair Details</h5>
                                                <Row>
                                                    <Col md={6}>
                                                        <Input
                                                            label="Completion Date & Time"
                                                            placeholder=""
                                                            type="datetime-local"
                                                            name="complete_at"
                                                            value={values.complete_at}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            error={errors.complete_at}
                                                            touched={touched.complete_at}
                                                            minValue={values.start_at ? `${values.start_at}T00:00` : undefined}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Input
                                                            label="Start Date (Optional)"
                                                            placeholder=""
                                                            type="date"
                                                            name="start_at"
                                                            value={values.start_at}
                                                            onChange={(event) => {
                                                                handleChange(event);
                                                                const nextStartDate = event.target.value;
                                                                if (
                                                                    nextStartDate &&
                                                                    values.complete_at &&
                                                                    new Date(values.complete_at).getTime() < new Date(nextStartDate).getTime()
                                                                ) {
                                                                    setFieldValue('complete_at', '');
                                                                }
                                                            }}
                                                            onBlur={handleBlur}
                                                            error={errors.start_at}
                                                            touched={touched.start_at}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Select
                                                            label="Tags"
                                                            placeholder="Type and press Enter to create tags"
                                                            options={tagsOptions}
                                                            loading={serviceTagsLoading}
                                                            name="tags"
                                                            value={values.tags}
                                                            onChange={(selectedOptions) => setFieldValue('tags', selectedOptions || [])}
                                                            onBlur={handleBlur}
                                                            isMulti={true}
                                                            isCreatable={true}
                                                            error={errors.tags && typeof errors.tags === 'string' ? errors.tags : null}
                                                            touched={touched.tags}
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>
                                        </div>

                                        {supportsServiceWorkflow ? (
                                            <>
                                                <IssueTracking
                                                    vehicleDetailLoading={currentAssetDetailLoading}
                                                    vehicleDetail={currentAssetDetail}
                                                    selectedVehicle={selectedAssetValue}
                                                    setLink_issue_ids={setLink_issue_ids}
                                                    link_issue_ids={link_issue_ids}
                                                    setUpdateIssue={setUpdateIssue}
                                                    serviceId={singleServiceData?.id}
                                                    assetType={currentAssetType}
                                                    assetId={getPayloadId(values.asset_id)}
                                                    assetLabel={currentAssetLabel}
                                                    refreshVehicleDetail={() => refreshVehicleDetail('issue')}
                                                    checkedIssueIds={checkedIssueIds}
                                                    setCheckedIssueIds={setCheckedIssueIds}
                                                />
                                                <ServiceLineItem
                                                    vehicleDetailLoading={currentAssetDetailLoading}
                                                    vehicleDetail={currentAssetDetail || singleServiceData}
                                                    selectedVehicle={selectedAssetValue}
                                                    assetType={currentAssetType}
                                                    assetId={getPayloadId(values.asset_id)}
                                                    assetLabel={currentAssetLabel}
                                                    refreshVehicleDetail={(serviceId) => refreshVehicleDetail('service', serviceId)}
                                                />
                                            </>
                                        ) : null}

                                        <div className='photo_document_section'>
                                            <h6>Photos & Documents</h6>
                                            <p>Upload relevant photos or documents for this service.</p>
                                            <FileUpload
                                                destination="service-entry"
                                                files={uploadedFiles}
                                                setFiles={setUploadedFiles}
                                                maxFileSize={10}
                                            />
                                        </div>

                                        <div className='d-flex align-items-center gap-2 my-4'>
                                            <Button
                                                cancelBtn={() => navigate('/services')}
                                                loading={updateServiceEntryLoading || isSubmitting}
                                                disabled={updateServiceEntryLoading || isSubmitting}
                                                submitText="Update Service Entry"
                                                type="submit"
                                                shouldRow={false}
                                                isShowSubmitBtn={canUpdate}
                                            />
                                        </div>
                                    </div>
                                    <div className='col-md-4'>
                                        <div className='service_cost_summary_details'>
                                            <div className='service_cost_summary'>
                                                <h6>Cost Summary</h6>
                                                <p>Financial breakdown of the service entries.</p>
                                                <div className='subtotal'>
                                                    <div>Subtotal (Calculated)</div>
                                                    <span>PKR {costSummary.sub_total}</span>
                                                </div>
                                                <div className='service_item'>
                                                    <div>Parts</div>
                                                    <div>PKR {costSummary.cost_parts}</div>
                                                </div>
                                                <div className='service_item'>
                                                    <div>Labor</div>
                                                    <div>PKR {costSummary.cost_labor}</div>
                                                </div>
                                                <div className="service_item">
                                                    <div>Discount (%)</div>
                                                    <div>
                                                        <input
                                                            className='edit_input'
                                                            type="number"
                                                            min={0}
                                                            max={100}
                                                            value={discountPercent}
                                                            onChange={handleDiscountChange}
                                                            placeholder='0'
                                                        /> %
                                                    </div>
                                                </div>
                                                <div className="service_item">
                                                    <div>Tax (%)</div>
                                                    <div>
                                                        <input
                                                            className='edit_input'
                                                            type="number"
                                                            min={0}
                                                            max={100}
                                                            value={taxPercent}
                                                            onChange={handleTaxChange}
                                                            placeholder='0'
                                                        /> %
                                                    </div>
                                                </div>
                                                <div className='total'>
                                                    <div>Total</div>
                                                    <span>PKR {totalAmount}</span>
                                                </div>
                                            </div>
                                            <div className='general_notes'>
                                                <h6>General Notes</h6>
                                                <p>Add any additional notes or observations here.</p>
                                                <textarea
                                                    placeholder='Type your general notes here...'
                                                    rows={7}
                                                    name="note"
                                                    value={values.note}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                                {touched.note && errors.note && (
                                                    <div className="error-message" style={{ color: '#ff0000de', fontSize: '12px', marginTop: "3px" }}>
                                                        {errors.note}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                    );
                }}
            </Formik>
        </>
    );
};

export default EditServiceEntry;
